import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, User, Lock, LogIn, UserPlus, CheckCircle, Clock } from 'lucide-react';
import { fetchLocationsForSignup, registerCoachPortal } from '../../services/coachAPI';

const SPORTS = [
  'Soccer',
  'Basketball',
  'Baseball',
  'Track & Field',
  'Flag Football',
  'Kickball',
  'Golf',
  'Cheer'
];

const getSportIcon = (sport) => {
  const icons = {
    Soccer: '⚽',
    Basketball: '🏀',
    Baseball: '⚾',
    Football: '🏈',
    'Track & Field': '🏃',
    'Flag Football': '🏈',
    Kickball: '⚾',
    Golf: '⛳',
    Cheer: '📣'
  };
  return icons[sport] || '🏅';
};

const CoachSignup = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    primarySport: 'Soccer',
    secondarySports: [],
    experience: '',
    yearsExperience: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const locs = await fetchLocationsForSignup();
        if (!cancelled) setLocations(locs);
      } catch (e) {
        console.warn('Locations list unavailable; manual entry still allowed.', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleSecondarySport = (sport) => {
    if (sport === formData.primarySport) return;
    const current = formData.secondarySports || [];
    const next = current.includes(sport)
      ? current.filter((s) => s !== sport)
      : [...current, sport];
    setFormData((prev) => ({ ...prev, secondarySports: next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!formData.location?.trim()) {
      setError('Please select or enter your primary location.');
      return;
    }

    setLoading(true);
    try {
      await registerCoachPortal({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone: formData.phone.trim(),
        location: formData.location.trim(),
        primarySport: formData.primarySport,
        secondarySports: formData.secondarySports,
        experience: formData.experience.trim(),
        yearsExperience: formData.yearsExperience
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 sm:p-10 border border-gray-100">
          <div className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" aria-hidden />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Account created</h1>
            <p className="text-gray-700 text-base leading-relaxed">
              Your coach account was created successfully. Before you can sign in, a YAU administrator must
              review and approve your registration.
            </p>
          </div>

          <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/80 p-5">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Clock className="h-5 w-5 text-amber-700" aria-hidden />
              </div>
              <div className="text-left space-y-2">
                <p className="font-semibold text-amber-950">Waiting for approval</p>
                <p className="text-sm text-amber-900/90 leading-relaxed">
                  You will not be able to log in until your account has been approved. After approval, use
                  the email and password you registered with on the sign-in page.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            If you have questions, contact{' '}
            <a href="mailto:coaches@yauapp.com" className="text-blue-600 hover:text-blue-800 font-medium">
              coaches@yauapp.com
            </a>
            .
          </p>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-8 w-full py-3 rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 font-medium shadow-lg transition-all"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-10 px-4">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
            <UserPlus className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Coach sign up</h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your account. An administrator must approve it before you can access the coach dashboard.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name *</label>
                <input
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name *</label>
                <input
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  required
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years of experience</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.yearsExperience}
                  onChange={(e) => setFormData({ ...formData, yearsExperience: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary location *</label>
              <input
                required
                list="coach-signup-locations"
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Select or type your location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                disabled={loading}
              />
              <datalist id="coach-signup-locations">
                {locations.map((loc) => (
                  <option key={loc} value={loc} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary sport *</label>
              <select
                required
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                value={formData.primarySport}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    primarySport: e.target.value,
                    secondarySports: (prev.secondarySports || []).filter((s) => s !== e.target.value)
                  }))
                }
                disabled={loading}
              >
                {SPORTS.map((s) => (
                  <option key={s} value={s}>
                    {getSportIcon(s)} {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary sports (optional)</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SPORTS.filter((s) => s !== formData.primarySport).map((sport) => (
                  <button
                    key={sport}
                    type="button"
                    onClick={() => toggleSecondarySport(sport)}
                    className={`p-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      formData.secondarySports?.includes(sport)
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    disabled={loading}
                  >
                    <span>{getSportIcon(sport)}</span>
                    <span className="truncate">{sport}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience &amp; background</label>
              <textarea
                rows={3}
                className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief coaching background, certifications, playing experience…"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 shadow-lg"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <LogIn className="h-5 w-5 text-white" />
                )}
              </span>
              {loading ? 'Submitting…' : 'Submit registration'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 font-medium hover:text-blue-800">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachSignup;
